import { useQuery } from "@tanstack/react-query";

import { formatCents } from "@/lib/money";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import type { Campaign } from "./campaignModel";

interface VariantRow {
  variant: "a" | "b";
  recipients: number;
  delivered: number;
  contacts_clicked: number;
  contacts_replied: number;
}

interface Performance {
  recipients: number;
  delivered: number;
  suppressed: number;
  failed: number;
  contacts_clicked: number;
  total_clicks: number;
  contacts_replied: number;
  revenue_after_send_cents: number;
  customers_invoiced: number;
}

const Metric = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper?: string;
}) => (
  <div className="min-w-0 rounded-md border border-border-subtle px-3 py-2">
    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <p className="text-[16px] font-semibold tabular-nums text-foreground">
      {value}
    </p>
    {helper ? (
      <p className="truncate text-[11.5px] text-muted-foreground">{helper}</p>
    ) : null}
  </div>
);

/**
 * What the send actually did, from ledgers that already exist.
 *
 * "Revenue after send" is labelled exactly that: invoices issued to
 * recipients within 30 days are correlation the owner can judge, calling it
 * attribution would be a lie with a chart.
 */
export const CampaignPerformance = ({ campaign }: { campaign: Campaign }) => {
  const { data } = useQuery<Performance>({
    queryKey: ["campaign-performance", campaign.id],
    enabled: campaign.status === "sent",
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "campaign_performance",
        { p_campaign_id: campaign.id },
      );
      if (error) throw new Error(error.message);
      return data as Performance;
    },
  });

  const { data: variants } = useQuery<VariantRow[]>({
    queryKey: ["campaign-variants", campaign.id],
    enabled: campaign.status === "sent" && Boolean(campaign.variant_body),
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "campaign_variant_performance",
        { p_campaign_id: campaign.id },
      );
      if (error) throw new Error(error.message);
      return data as VariantRow[];
    },
  });

  const hasVariant = Boolean(campaign.variant_body);

  if (campaign.status !== "sent" || !data) return null;

  const pct = (part: number) =>
    data.delivered === 0
      ? "n/a"
      : `${Math.round((part / data.delivered) * 100)}%`;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-[13px] font-semibold text-foreground">Performance</h3>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Metric
          label="Delivered"
          value={`${data.delivered}/${data.recipients}`}
          helper={
            data.suppressed > 0
              ? `${data.suppressed} suppressed by consent`
              : undefined
          }
        />
        <Metric
          label="Clicked"
          value={pct(data.contacts_clicked)}
          helper={`${data.contacts_clicked} contact${
            data.contacts_clicked === 1 ? "" : "s"
          }, ${data.total_clicks} click${data.total_clicks === 1 ? "" : "s"}`}
        />
        <Metric
          label="Replied"
          value={pct(data.contacts_replied)}
          helper={`${data.contacts_replied} conversation${
            data.contacts_replied === 1 ? "" : "s"
          } started`}
        />
        <Metric
          label="Revenue after send"
          value={formatCents(data.revenue_after_send_cents)}
          helper={`${data.customers_invoiced} recipient${
            data.customers_invoiced === 1 ? "" : "s"
          } invoiced within 30 days, correlation, not attribution`}
        />
        {data.failed > 0 ? (
          <Metric label="Failed" value={String(data.failed)} />
        ) : null}
      </div>

      {hasVariant && variants && variants.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-border-subtle">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-border-subtle bg-muted/40 text-left">
                <th className="px-2 py-1 font-medium">Variant</th>
                <th className="px-2 py-1 font-medium">Delivered</th>
                <th className="px-2 py-1 font-medium">Clicked</th>
                <th className="px-2 py-1 font-medium">Replied</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((row) => (
                <tr
                  key={row.variant}
                  className="border-b border-border-subtle/60"
                >
                  <td className="px-2 py-1 font-medium uppercase">
                    {row.variant}
                  </td>
                  <td className="px-2 py-1 tabular-nums">
                    {row.delivered}/{row.recipients}
                  </td>
                  <td className="px-2 py-1 tabular-nums">
                    {row.contacts_clicked}
                  </td>
                  <td className="px-2 py-1 tabular-nums">
                    {row.contacts_replied}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-2 py-1 text-[11px] text-muted-foreground">
            Small samples: judge the numbers, not a declared winner.
          </p>
        </div>
      ) : null}
    </div>
  );
};
